import { useEffect, useId, useRef, useState } from "react";

import {
  AI_PROVIDERS,
  accountErrorMessage,
  createProviderConnection,
  disconnectProviderConnection,
  isActiveProviderConnectionLifecycle,
  isAiProvider,
  isManagedAiUnavailableError,
  listProviderConnections,
  previewProviderConnectionDisconnect,
  rotateProviderConnection,
  type AiProvider,
  type ProviderConnection,
  type ProviderConnectionDisconnectImpact,
  type ProviderConnectionLifecycle,
} from "../../lib/account-client";

export interface ProviderConnectionsApi {
  listProviderConnections: typeof listProviderConnections;
  createProviderConnection: typeof createProviderConnection;
  rotateProviderConnection: typeof rotateProviderConnection;
  previewProviderConnectionDisconnect: typeof previewProviderConnectionDisconnect;
  disconnectProviderConnection: typeof disconnectProviderConnection;
}

export const providerConnectionsApi: ProviderConnectionsApi = {
  listProviderConnections,
  createProviderConnection,
  rotateProviderConnection,
  previewProviderConnectionDisconnect,
  disconnectProviderConnection,
};

export function ProviderConnections({
  workspaceId,
  api = providerConnectionsApi,
}: {
  workspaceId: string;
  api?: ProviderConnectionsApi;
}) {
  const credentialDialogRef = useRef<HTMLDialogElement>(null);
  const credentialFormRef = useRef<HTMLFormElement>(null);
  const disconnectDialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const errorId = useId();
  const [connections, setConnections] = useState<ProviderConnection[] | null>(null);
  const [editing, setEditing] = useState<ProviderConnection | null>(null);
  const [providerChoice, setProviderChoice] = useState<AiProvider>("anthropic");
  const [disconnecting, setDisconnecting] = useState<ProviderConnection | null>(null);
  const [impact, setImpact] = useState<ProviderConnectionDisconnectImpact | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    void api.listProviderConnections(workspaceId)
      .then(({ connections: value }) => {
        if (!active) return;
        setConnections(value);
        setAvailable(true);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (isManagedAiUnavailableError(cause)) {
          setConnections([]);
          setAvailable(false);
          return;
        }
        setError(accountErrorMessage(cause, "Doorway could not load your provider connections."));
      });
    return () => { active = false; };
  }, [api, workspaceId]);

  const openCredentialDialog = (trigger: HTMLButtonElement, connection: ProviderConnection | null = null) => {
    returnFocusRef.current = trigger;
    setEditing(connection);
    setProviderChoice(connection?.provider ?? firstAvailableProvider(activeProviders));
    setError(null);
    credentialDialogRef.current?.showModal();
  };

  const resetCredentialDialog = () => {
    credentialFormRef.current?.reset();
    setEditing(null);
    setError(null);
  };

  const closeCredentialDialog = () => {
    resetCredentialDialog();
    credentialDialogRef.current?.close();
    returnFocusRef.current?.focus();
  };

  const submitCredential = async (form: HTMLFormElement) => {
    if (pending) return;
    const data = new FormData(form);
    const apiKey = data.get("apiKey");
    const provider = editing?.provider ?? data.get("provider");
    if (typeof apiKey !== "string" || !isAiProvider(provider)) return;
    setPending(true);
    setError(null);
    setAnnouncement(editing ? `Rotating ${providerName(editing.provider)} connection.` : `Validating ${providerName(provider)} connection.`);
    try {
      const result = editing
        ? await api.rotateProviderConnection(editing.id, {
            workspaceId,
            apiKey: apiKey.trim(),
            connectionVersion: editing.version,
          })
        : await api.createProviderConnection({ workspaceId, provider, apiKey: apiKey.trim() });
      setConnections((current) => [
        ...(current ?? []).filter((item) => item.id !== result.connection.id && item.provider !== result.connection.provider),
        result.connection,
      ]);
      form.reset();
      credentialDialogRef.current?.close();
      setEditing(null);
      setAnnouncement(`${providerName(result.connection.provider)} is ${result.connection.lifecycle === "ready" ? "ready to use" : lifecycleLabel(result.connection.lifecycle).toLocaleLowerCase()}.`);
      returnFocusRef.current?.focus();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not connect this provider account."));
      const key = form.elements.namedItem("apiKey");
      if (key instanceof HTMLInputElement) {
        key.value = "";
        key.focus();
      }
      setAnnouncement("Provider connection failed. Review the error and try again.");
    } finally {
      setPending(false);
    }
  };

  const previewDisconnect = async (trigger: HTMLButtonElement, connection: ProviderConnection) => {
    if (pending) return;
    returnFocusRef.current = trigger;
    setPending(true);
    setError(null);
    setImpact(null);
    setDisconnecting(connection);
    setAnnouncement(`Checking projects that use ${providerName(connection.provider)}.`);
    try {
      const result = await api.previewProviderConnectionDisconnect(connection.id, workspaceId);
      setImpact(result);
      disconnectDialogRef.current?.showModal();
      setAnnouncement(`Disconnect review ready. ${result.affectedProjects.length} projects will be affected.`);
    } catch (cause) {
      setDisconnecting(null);
      setError(accountErrorMessage(cause, "Doorway could not check the disconnect impact."));
      setAnnouncement("Disconnect review failed.");
    } finally {
      setPending(false);
    }
  };

  const closeDisconnectDialog = () => {
    disconnectDialogRef.current?.close();
    setDisconnecting(null);
    setImpact(null);
    setError(null);
    returnFocusRef.current?.focus();
  };

  const confirmDisconnect = async () => {
    if (!disconnecting || !impact || pending) return;
    setPending(true);
    setError(null);
    setAnnouncement(`Disconnecting ${providerName(disconnecting.provider)} and pausing affected AI features.`);
    try {
      await api.disconnectProviderConnection(disconnecting.id, {
        workspaceId,
        confirmationToken: impact.confirmationToken,
        connectionVersion: impact.connectionVersion,
        affectedProjectIds: impact.affectedProjects.map((project) => project.id),
      });
      setConnections((current) => (current ?? []).filter((item) => item.id !== disconnecting.id));
      disconnectDialogRef.current?.close();
      setAnnouncement(`${providerName(disconnecting.provider)} disconnected. Affected sites remain online with AI paused.`);
      setDisconnecting(null);
      setImpact(null);
      returnFocusRef.current?.focus();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not disconnect this provider account."));
      setAnnouncement("Disconnect failed. Review the error and try again.");
    } finally {
      setPending(false);
    }
  };

  const activeProviders = new Set((connections ?? [])
    .filter((item) => isActiveProviderConnectionLifecycle(item.lifecycle))
    .map((item) => item.provider));

  return (
    <div className="provider-connections">
      <div className="provider-connections-heading">
        <div>
          <p className="step-label">Reusable accounts</p>
          <h2>AI provider connections</h2>
          <p>Connect each provider once, then reuse it across projects you own. Your provider bills you directly for model usage.</p>
        </div>
        {available !== false ? (
          <button className="primary-button" type="button" disabled={connections === null || activeProviders.size === 3} onClick={(event) => openCredentialDialog(event.currentTarget)}>
            Add provider connection
          </button>
        ) : null}
      </div>

      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      {error && !credentialDialogRef.current?.open && !disconnectDialogRef.current?.open && <p className="error-message" role="alert">{error}</p>}
      {connections === null && !error ? <p className="workspace-empty" role="status">Loading provider connections...</p> : null}
      {available === false ? <p className="workspace-empty">Managed AI connections are unavailable for this workspace.</p> : null}
      {available !== false && connections?.length === 0 ? <p className="workspace-empty">No provider accounts connected yet.</p> : null}
      {connections && connections.length > 0 ? (
        <ul className="provider-connection-list">
          {connections.map((connection) => (
            <li key={connection.id}>
              <div className="provider-connection-summary">
                <span className={`provider-status provider-status-${connection.lifecycle}`}>{lifecycleLabel(connection.lifecycle)}</span>
                <strong>{providerName(connection.provider)}</strong>
                <small>{connection.affectedProjects.length} {connection.affectedProjects.length === 1 ? "project" : "projects"} using this connection</small>
                {connection.message && <p>{connection.message}</p>}
              </div>
              <div className="provider-connection-actions">
                <button className="text-button" type="button" disabled={pending || !canRotate(connection.lifecycle)} onClick={(event) => openCredentialDialog(event.currentTarget, connection)}>Rotate key</button>
                <button className="text-button danger-button" type="button" disabled={pending || !canDisconnect(connection.lifecycle)} onClick={(event) => void previewDisconnect(event.currentTarget, connection)}>Disconnect {providerName(connection.provider)}</button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <dialog
        className="ai-settings-dialog"
        ref={credentialDialogRef}
        aria-labelledby="provider-credential-heading"
        onCancel={(event) => { if (pending) event.preventDefault(); }}
        onClose={() => { resetCredentialDialog(); returnFocusRef.current?.focus(); }}
      >
        <form ref={credentialFormRef} onSubmit={(event) => { event.preventDefault(); void submitCredential(event.currentTarget); }}>
          <div className="ai-settings-heading">
            <div><p className="step-label">Private credential</p><h3 id="provider-credential-heading">{editing ? `Rotate ${providerName(editing.provider)}` : "Add provider connection"}</h3></div>
            <button type="button" className="dialog-close" aria-label="Close provider connection" disabled={pending} onClick={closeCredentialDialog}>×</button>
          </div>
          <p>The key is used only to create your private provider connection. It will not be shown again or included in your project files.</p>
          {error && <p className="error-message" id={errorId} role="alert">{error}</p>}
          <label><span>Provider</span><select name="provider" aria-describedby={error ? errorId : undefined} value={providerChoice} onChange={(event) => setProviderChoice(event.target.value as AiProvider)} disabled={pending || Boolean(editing)}><ProviderOptions activeProviders={activeProviders} editing={editing} /></select></label>
          <label><span>API key</span><input name="apiKey" type="password" required minLength={20} maxLength={1024} autoComplete="new-password" aria-describedby={error ? errorId : undefined} disabled={pending} /></label>
          <p className="provider-billing-note">Anthropic, OpenAI, or Google will charge your provider account. Doorway does not resell model usage.</p>
          <div className="ai-settings-actions">
            <button type="button" className="text-button" disabled={pending} onClick={closeCredentialDialog}>Cancel</button>
            <button type="submit" className="primary-button" disabled={pending}>{pending ? "Validating..." : editing ? "Rotate key" : "Connect account"}</button>
          </div>
        </form>
      </dialog>

      <dialog
        className="ai-settings-dialog provider-disconnect-dialog"
        ref={disconnectDialogRef}
        aria-labelledby="provider-disconnect-heading"
        onCancel={(event) => { if (pending) event.preventDefault(); }}
        onClose={() => {
          if (!pending) {
            setDisconnecting(null);
            setImpact(null);
            setError(null);
            returnFocusRef.current?.focus();
          }
        }}
      >
        <div className="provider-disconnect-content">
          <div className="ai-settings-heading">
            <div><p className="step-label">Review impact</p><h3 id="provider-disconnect-heading">Disconnect {providerName(disconnecting?.provider)}</h3></div>
            <button type="button" className="dialog-close" aria-label="Close disconnect review" disabled={pending} onClick={closeDisconnectDialog}>×</button>
          </div>
          <p>AI will pause for every project below. Each site stays online, and you can reconnect later.</p>
          {error && <p className="error-message" role="alert">{error}</p>}
          {impact?.affectedProjects.length ? <ul className="provider-impact-list">{impact.affectedProjects.map((project) => <li key={project.id}><strong>{project.name}</strong><span>{project.paused ? "AI already paused" : "AI will pause"}</span></li>)}</ul> : <p className="workspace-empty">No projects currently use this connection.</p>}
          <div className="ai-settings-actions">
            <button type="button" className="text-button" disabled={pending} onClick={closeDisconnectDialog}>Cancel</button>
            <button type="button" className="danger-action-button" disabled={pending || !impact} onClick={() => void confirmDisconnect()}>{pending ? "Disconnecting..." : "Disconnect and pause AI"}</button>
          </div>
        </div>
      </dialog>
    </div>
  );
}

function ProviderOptions({ activeProviders, editing }: { activeProviders: Set<AiProvider>; editing: ProviderConnection | null }) {
  return <>{AI_PROVIDERS.map((provider) => <option key={provider} value={provider} disabled={!editing && activeProviders.has(provider)}>{providerName(provider)}</option>)}</>;
}

function firstAvailableProvider(activeProviders: Set<AiProvider>): AiProvider {
  return AI_PROVIDERS.find((provider) => !activeProviders.has(provider)) ?? "anthropic";
}

export function providerName(provider: AiProvider | undefined): string {
  if (provider === "openai") return "OpenAI";
  if (provider === "google") return "Google";
  return "Anthropic";
}

function lifecycleLabel(lifecycle: ProviderConnectionLifecycle): string {
  const labels: Record<ProviderConnectionLifecycle, string> = {
    validating: "Validating",
    provisioning: "Provisioning",
    ready: "Ready",
    rotating: "Rotating, prior key active",
    retryable_failure: "Needs attention",
    capacity_blocked: "Connection capacity reached",
    revocation_pending: "Disconnecting",
    disconnected: "Disconnected",
    paused: "Paused",
  };
  return labels[lifecycle];
}

function canRotate(lifecycle: ProviderConnectionLifecycle): boolean {
  return lifecycle === "ready" || lifecycle === "rotating" || lifecycle === "paused";
}

function canDisconnect(lifecycle: ProviderConnectionLifecycle): boolean {
  return lifecycle === "ready"
    || lifecycle === "rotating"
    || lifecycle === "retryable_failure"
    || lifecycle === "paused";
}
